import CmsImage from '@/components/ui/CmsImage';
import { stripHtml } from '@/lib/homepage-mappers';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { getStrapiOriginalMediaUrl } from '@/lib/strapi-media-url';
import { getCmsButtonLinkProps } from '@/lib/cms-button-target';
import type { CompanyProfileButton } from '@/types/company-profile';
import type { InfraStrapiMedia } from '@/services/infrastructure-project.service';
import CompanyProfileDownloadCta from './CompanyProfileDownloadCta';
import CmsRichTextHeading, { hasRichTextContent } from '@/components/ui/CmsRichTextHeading';

interface CompanyProfileHeroProps {
  locale: string;
  bannerHeading?: string | null;
  titleRichText?: string | null;
  bannerSubheading?: string | null;
  bannerImage?: InfraStrapiMedia | null;
  bannerButton?: CompanyProfileButton[];
}

export default function CompanyProfileHero({
  locale: _locale,
  bannerHeading,
  titleRichText,
  bannerSubheading,
  bannerImage,
  bannerButton = [],
}: CompanyProfileHeroProps) {
  const heading = bannerHeading?.trim() || '';
  const subHeadingHtml = bannerSubheading?.trim() || '';
  const cta = bannerButton[0];
  const ctaLabel = cta?.buttonText?.trim() || '';
  const ctaAria = cta?.ariaLabel?.trim() || ctaLabel;
  const ctaHref = cta?.buttonUrl?.trim() || '';
  const bannerImageUrl = getStrapiOriginalMediaUrl(bannerImage) || MEDIA_PLACEHOLDER;
  const imageAlt =
    bannerImage?.alternativeText?.trim() || stripHtml(heading) || 'Wonder Cement infrastructure';

  const heroContent = (
    <div className="w-full max-w-[80ch]">
      {heading || hasRichTextContent(titleRichText) ? (
        <CmsRichTextHeading
          className="hero-title text-white"
          html={titleRichText}
          fallback={heading}
        />
      ) : null}

      {subHeadingHtml ? (
        <div
          className="hero-subtitle mt-4 max-w-[520px] text-white/80 sm:mt-5 [&_p]:m-0"
          dangerouslySetInnerHTML={{ __html: subHeadingHtml }}
        />
      ) : null}

      {ctaLabel ? (
        <div className="mt-6 sm:mt-8 lg:mt-10">
          <CompanyProfileDownloadCta
            href={ctaHref || '#'}
            text={ctaLabel}
            ariaLabel={ctaAria}
            {...getCmsButtonLinkProps(cta)}
            className="w-full justify-center !whitespace-normal text-center sm:w-auto sm:justify-start sm:!whitespace-nowrap sm:text-left"
          />
        </div>
      ) : null}
    </div>
  );

  return (
    <>
      <div className="flex flex-col bg-black text-white lg:hidden">
        <div className="relative w-full shrink-0">
          <CmsImage
            src={bannerImageUrl}
            alt={imageAlt}
            width={1920}
            height={1080}
            priority
            loading="eager"
            className="h-auto w-full"
            sizes="100vw"
          />
        </div>
        <div className="container mx-auto py-10 sm:py-12 max-sm:px-4">{heroContent}</div>
      </div>

      <section className="relative hidden min-h-[600px] h-[100dvh] w-full overflow-hidden bg-black text-white lg:block">
        <div className="absolute inset-0">
          <CmsImage
            src={bannerImageUrl}
            alt={imageAlt}
            fill
            priority
            loading="eager"
            className="object-cover object-center"
            sizes="100vw"
          />
          <div
            aria-hidden
            className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-black/10"
          />
          <div
            aria-hidden
            className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30"
          />
        </div>

        <div className="relative z-10 container mx-auto flex h-full flex-col justify-end py-12 md:py-14 lg:py-16">
          {heroContent}
        </div>
      </section>
    </>
  );
}
